import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { OptimizedImage } from "@/components/ui/optimized-image";
import {
  Radio,
  Waves,
  Sparkles,
  Droplets,
  Wrench,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";

export interface FeatureModule {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  tagline: string;
  title: string;
  description: string;
  bullets: string[];
  image?: { src: string; alt: string };
  metric?: { value: string; label: string };
}

interface Rem7HydraFeatureShowcaseProps {
  shortName: string;
  modules: FeatureModule[];
}

/**
 * Premium tabbed feature showcase for REM7 Hydra whirlpool products.
 * Mirrors the Apple / Dyson product-page pattern: a single coherent module
 * frame, sticky-feeling tab toggles, large product imagery, headline metric,
 * and a clean ranked bullet list. Designed for clarity and conviction.
 */
export const Rem7HydraFeatureShowcase = ({
  shortName,
  modules,
}: Rem7HydraFeatureShowcaseProps) => {
  const [active, setActive] = useState(modules[0]?.id);
  if (!modules.length) return null;

  return (
    <section
      id="features"
      className="border-b border-neutral-200 bg-gradient-to-b from-neutral-50 via-white to-neutral-50 py-20"
    >
      <div className="container mx-auto px-6 lg:px-12">
        {/* HEADER */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">
            Engineering Showcase
          </p>
          <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
            Inside the {shortName}
          </h2>
          <div className="mx-auto mt-3 h-px w-16 bg-amber-500" />
          <p className="mt-5 text-neutral-600 leading-relaxed">
            Every system in the Hydra programme is engineered to be specified
            with confidence. Toggle between the modules to see exactly what is
            included, how it performs, and why it matters on the bathroom floor.
          </p>
        </div>

        {/* TABS */}
        <Tabs
          value={active}
          onValueChange={setActive}
          className="mt-12"
        >
          <TabsList
            className="mx-auto flex h-auto w-full max-w-5xl flex-wrap justify-center gap-2 rounded-full border border-neutral-200 bg-white/80 p-2 shadow-sm backdrop-blur"
          >
            {modules.map((m) => (
              <TabsTrigger
                key={m.id}
                value={m.id}
                className="group flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium uppercase tracking-[0.15em] text-neutral-600 transition-all data-[state=active]:bg-neutral-900 data-[state=active]:text-white data-[state=active]:shadow-md"
              >
                <m.icon className="h-3.5 w-3.5 text-amber-600 group-data-[state=active]:text-amber-300" />
                <span>{m.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {modules.map((m) => (
            <TabsContent
              key={m.id}
              value={m.id}
              className="mt-10 focus-visible:outline-none"
            >
              <div className="mx-auto grid max-w-6xl gap-10 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm lg:grid-cols-2">
                {/* IMAGE */}
                <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100 lg:aspect-auto">
                  {m.image ? (
                    <OptimizedImage
                      src={m.image.src}
                      alt={m.image.alt}
                      width={900}
                      height={700}
                      className="h-full w-full"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-neutral-100 text-neutral-400">
                      <m.icon className="h-16 w-16" />
                    </div>
                  )}
                  {m.metric && (
                    <div className="absolute bottom-4 left-4 rounded-lg border border-amber-500/40 bg-neutral-900/90 px-4 py-3 text-white shadow-lg backdrop-blur">
                      <p className="font-serif text-2xl font-bold leading-none text-amber-300">
                        {m.metric.value}
                      </p>
                      <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-neutral-300">
                        {m.metric.label}
                      </p>
                    </div>
                  )}
                </div>

                {/* CONTENT */}
                <div className="flex flex-col justify-center p-8 lg:p-10">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-600">
                    {m.tagline}
                  </p>
                  <h3 className="mt-3 font-serif text-2xl font-bold text-neutral-900 sm:text-3xl">
                    {m.title}
                  </h3>
                  <div className="mt-3 h-px w-12 bg-amber-500" />
                  <p className="mt-5 text-neutral-600 leading-relaxed">
                    {m.description}
                  </p>
                  <ul className="mt-6 space-y-3">
                    {m.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-sm text-neutral-700"
                      >
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                        <span className="leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* MODULE STRIP */}
        <div className="mx-auto mt-10 grid max-w-6xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {modules.map((m) => {
            const isActive = m.id === active;
            return (
              <button
                key={`strip-${m.id}`}
                type="button"
                onClick={() => setActive(m.id)}
                className={`group flex flex-col items-center gap-2 rounded-xl border p-4 text-center transition-all ${
                  isActive
                    ? "border-amber-500 bg-amber-50/60 shadow-sm"
                    : "border-neutral-200 bg-white hover:border-amber-300"
                }`}
              >
                <m.icon
                  className={`h-5 w-5 transition-colors ${
                    isActive
                      ? "text-amber-600"
                      : "text-neutral-500 group-hover:text-amber-600"
                  }`}
                />
                <span
                  className={`text-[11px] font-medium uppercase tracking-[0.12em] ${
                    isActive ? "text-neutral-900" : "text-neutral-600"
                  }`}
                >
                  {m.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* Re-export icons to make Rem7WhirlpoolDeluxe imports tidy. */
export const HydraIcons = {
  Radio,
  Waves,
  Sparkles,
  Droplets,
  Wrench,
  ShieldCheck,
};
